import InsightsIcon from '@mui/icons-material/Insights'
import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { useAgentSummary, useRevisionsSummary, useStrategySummary, useTopPrompts } from 'api/aws/kpi/useKPI'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useState } from 'react'

// ── Loading Indicator ─────────────────────────────────────────────────────────

const LoadingOverlay = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress size={40} />
  </Box>
)

const NoDataMessage = ({ message = 'No data yet' }: { message?: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

const formatCurrency = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })

const TYPE_COLORS: Record<string, string> = {
  patch: '#66bb6a',
  minor: '#ffa726',
  major: '#ef5350',
}

const STATUS_COLORS: Record<string, string> = {
  Deployed: '#66bb6a',
  Draft: '#90a4ae',
  Review: '#ffa726',
}

// ── Category Colors ───────────────────────────────────────────────────────────

const CATEGORY_BORDER: Record<string, string> = {
  agent: '#42a5f5',
  revisions: '#ffa726',
  strategy: '#66bb6a',
}

// ── Summary Card ──────────────────────────────────────────────────────────────

const SummaryCard = ({
  title,
  value,
  subtitle,
  category,
}: {
  title: string
  value: string | number
  subtitle?: string
  category: 'agent' | 'revisions' | 'strategy'
}) => {
  const theme = useTheme()
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderLeft: `4px solid ${CATEGORY_BORDER[category]}`,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderLeftColor: CATEGORY_BORDER[category],
        borderLeftWidth: 4,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  )
}

// ── Section Table Wrapper ─────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, mt: 3 }}>
    {children}
  </Typography>
)

// ── Tab Panel ─────────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
)

// ── Agent Tab ─────────────────────────────────────────────────────────────────

const AgentTab = () => {
  const theme = useTheme()
  const { data: summary, isLoading: summaryLoading } = useAgentSummary()
  const { data: topPrompts, isLoading: promptsLoading } = useTopPrompts()

  if (summaryLoading && promptsLoading) return <LoadingOverlay />

  const totalConversations = summary?.totalConversations ?? 0
  const totalTokens = summary?.totalTokens ?? 0
  const totalCost = summary?.totalCost ?? 0
  const avgTokens = summary?.avgTokensPerConversation ?? 0
  const conversations = summary?.recentConversations ?? []
  const prompts = topPrompts ?? []

  if (totalConversations === 0 && prompts.length === 0) return <NoDataMessage />

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="agent" title="Total Conversations" value={totalConversations.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="agent" title="Total Tokens Used" value={formatTokens(totalTokens)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="agent" title="Total Cost" value={formatCurrency(totalCost)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="agent" title="Avg Tokens / Conversation" value={formatTokens(avgTokens)} />
        </Grid>
      </Grid>

      <SectionTitle>Most Used Prompts</SectionTitle>
      {promptsLoading ? (
        <LoadingOverlay />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Prompt</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  Times Used
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  Last Used
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.map(p => (
                <TableRow key={p.label} hover>
                  <TableCell sx={{ fontSize: 13 }}>{p.label}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {p.count}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {p.lastUsed}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SectionTitle>Recent Conversations</SectionTitle>
      {summaryLoading ? (
        <LoadingOverlay />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Message Preview</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  Tokens
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  Cost
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  Tool Calls
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((c, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{c.createdAt}</TableCell>
                  <TableCell
                    sx={{
                      fontSize: 13,
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.firstMessage}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {formatTokens(c.totalTokens)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {formatCurrency(c.totalCost)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {c.toolCallCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

// ── Revisions Tab ─────────────────────────────────────────────────────────────

const RevisionsTab = () => {
  const theme = useTheme()
  const { data, isLoading } = useRevisionsSummary()

  if (isLoading) return <LoadingOverlay />

  const totalRevisions = data?.totalRevisions ?? 0
  const patchCount = data?.countByType?.patch ?? 0
  const minorCount = data?.countByType?.minor ?? 0
  const majorCount = data?.countByType?.major ?? 0
  const revenueImpact = data?.revenueImpact ?? 0
  const mostRevised = data?.mostRevisedOrgs ?? []
  const recent = data?.recentRevisions ?? []
  const maxBarValue = Math.max(patchCount, minorCount, majorCount, 1)

  if (totalRevisions === 0) return <NoDataMessage />

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="revisions" title="Total Versions Created" value={totalRevisions.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            category="revisions"
            title="Version Breakdown"
            value={`${patchCount} / ${minorCount} / ${majorCount}`}
            subtitle="Patch / Minor / Major"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="revisions" title="Revenue Impact" value={formatCurrency(revenueImpact)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="revisions" title="Total Revisions" value={totalRevisions.toLocaleString()} />
        </Grid>
      </Grid>

      <SectionTitle>Versions by Type</SectionTitle>
      <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {[
          { label: 'Patch', count: patchCount, color: TYPE_COLORS.patch },
          { label: 'Minor', count: minorCount, color: TYPE_COLORS.minor },
          { label: 'Major', count: majorCount, color: TYPE_COLORS.major },
        ].map(item => (
          <Box key={item.label} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                {item.count}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(item.count / maxBarValue) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: theme.palette.action.hover,
                '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 4 },
              }}
            />
          </Box>
        ))}
      </Paper>

      <SectionTitle>Most Revised Organizations</SectionTitle>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Organization</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Versions
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Latest
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Type
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mostRevised.map(r => (
              <TableRow key={r.pk} hover>
                <TableCell sx={{ fontSize: 13 }}>{r.name}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {r.count}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                  —
                </TableCell>
                <TableCell align="right">—</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SectionTitle>Recent Revisions</SectionTitle>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Deal</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Version
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Type
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recent.map(r => (
              <TableRow key={`${r.PK}_${r.SK}`} hover>
                <TableCell sx={{ fontSize: 13 }}>{r.PK?.replace('ORG_', '') || '—'}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.name || '—'}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                  {r.semanticVersion || '—'}
                </TableCell>
                <TableCell align="right">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: TYPE_COLORS[r.versionChangeType] || 'text.secondary',
                      textTransform: 'capitalize',
                    }}
                  >
                    {r.versionChangeType || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

// ── Strategy Tab ──────────────────────────────────────────────────────────────

const StrategyTab = () => {
  const theme = useTheme()
  const { data, isLoading } = useStrategySummary()

  if (isLoading) return <LoadingOverlay />

  const totalCreated = data?.totalCreated ?? 0
  const totalDeployed = data?.totalDeployed ?? 0
  const conversionRate = data?.conversionRate ?? 0
  const avgPoints = data?.avgPoints ?? 0
  const recentStrategies = data?.recentStrategies ?? []
  const aiRecommended = data?.itemBreakdown?.aiRecommended ?? 0
  const manual = data?.itemBreakdown?.manual ?? 0
  const totalItems = aiRecommended + manual
  const acceptanceRate = totalItems > 0 ? Math.round((aiRecommended / totalItems) * 100 * 10) / 10 : 0
  const deploySuccessRate = totalCreated > 0 ? Math.round((totalDeployed / totalCreated) * 100 * 10) / 10 : 0

  if (totalCreated === 0) return <NoDataMessage />

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="strategy" title="Strategies Created" value={totalCreated} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            category="strategy"
            title="Strategies Deployed"
            value={totalDeployed}
            subtitle={`${conversionRate}% conversion`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard category="strategy" title="Avg Points / Strategy" value={avgPoints} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            category="strategy"
            title="AI Recommendations Accepted"
            value={`${acceptanceRate}%`}
            subtitle="acceptance rate"
          />
        </Grid>
      </Grid>

      <SectionTitle>Recent Strategies</SectionTitle>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Points
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Items
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                Created
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentStrategies.map((s, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontSize: 13 }}>{s.clientName}</TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    sx={{ fontSize: 11, fontWeight: 600, color: STATUS_COLORS[s.status] || 'text.secondary' }}
                  >
                    {s.status}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {s.points}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {s.items}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  {s.createdAt}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SectionTitle>Deploy Success Rate</SectionTitle>
      <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Success Rate
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: CATEGORY_BORDER.strategy }}>
            {deploySuccessRate}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={deploySuccessRate}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: theme.palette.action.hover,
            '& .MuiLinearProgress-bar': { bgcolor: CATEGORY_BORDER.strategy, borderRadius: 5 },
          }}
        />
      </Paper>
    </>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

const KPIDashboard = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const [tabIndex, setTabIndex] = useState(0)

  return (
    <Box
      sx={{
        transition: 'padding-top 0.3s ease',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'auto',
        bgcolor: theme.palette.background.default,
        px: { xs: 2, md: 4 },
        py: 3,
        pt: pinned ? '60px' : 0,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <InsightsIcon sx={{ fontSize: 28, color: 'rgb(255, 167, 38)' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Performance Dashboard
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Key performance indicators across AI Agent usage, deal versioning, and strategy builder activity.
      </Typography>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14 },
        }}
      >
        <Tab label="AI Agent" sx={{ color: tabIndex === 0 ? CATEGORY_BORDER.agent : 'text.secondary' }} />
        <Tab label="Revisions" sx={{ color: tabIndex === 1 ? CATEGORY_BORDER.revisions : 'text.secondary' }} />
        <Tab label="Strategy Builder" sx={{ color: tabIndex === 2 ? CATEGORY_BORDER.strategy : 'text.secondary' }} />
      </Tabs>

      <TabPanel value={tabIndex} index={0}>
        <AgentTab />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <RevisionsTab />
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <StrategyTab />
      </TabPanel>
    </Box>
  )
}

export default KPIDashboard
